import { Link } from 'react-router-dom';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { History, Pencil } from 'lucide-react';

import type { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { numberColumn, textColumn } from 'components/DataTable';
import { DecimalInput } from 'components/DecimalInput';
import { WarningIndicator } from 'components/WarningIndicator';

import { formatEuroAmount } from 'helpers/price';

import { getSupplyOrderProductReceivedTotal } from '../../shared/receivedDetails';
import {
    getExpectedQuantity,
    getOrderedQuantity,
    getProductLinePrice,
    SupplyOrderDetails,
    SupplyOrderFormValues,
    SupplyOrderProductFormValue,
} from '../supplyOrderFormValues';
import { getSupplyOrderProductWarnings } from '../supplyOrderProductWarnings';
import { StockVariationPreviewCell, SupplyOrderStockVariations } from '../StockVariationPreviewCell';

export type OrderedProductRow = SupplyOrderProductFormValue & { formIndex: number };

type ColumnsOptions = {
    /** Édition des quantités reçues : elle n'a de sens qu'une fois l'achat arrivé. La VALEUR reçue,
     * elle, se lit en permanence — c'est une information, pas une saisie. */
    canEditReceivedDetails: boolean;
    /** Quantité minimale imposée par le fournisseur, par produit. */
    minQuantityByProductId: Map<string, number>;
    /** Produits que Supply a demandés : l'achat devrait en porter au moins une unité. */
    productIdsToOrderOnce: Set<string>;
    onEditReceivedDetails: (formIndex: number) => void;
    onShowConfirmedQuantityHistory: (productId: string) => void;
    confirmedQuantityHistoryByProductId: Map<string, unknown[]>;
    stockVariations: SupplyOrderStockVariations;
    orderingTimes: SupplyOrderDetails['supplier']['orderingTimes'];
};

/** Quantité d'une ligne : des entiers, donc un vrai `type="number"`. Vide ⇒ `null` si nullable, `0` sinon. */
const NumberCell = ({
    name,
    nullable = false,
    dataTest,
    ariaLabel,
}: {
    name: `products.${number}.${'colis' | 'confirmedQuantity'}`;
    nullable?: boolean;
    dataTest: string;
    ariaLabel: string;
}) => {
    const { control } = useFormContext<SupplyOrderFormValues>();

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <Input
                    type="number"
                    min={0}
                    step="1"
                    className="h-7 w-24 text-right"
                    placeholder="0"
                    aria-label={ariaLabel}
                    data-test={dataTest}
                    value={field.value ?? ''}
                    onChange={(event) => {
                        const rawValue = event.target.value;
                        if (rawValue === '') {
                            field.onChange(nullable ? null : 0);
                            return;
                        }
                        const parsedValue = parseInt(rawValue, 10);
                        field.onChange(Number.isNaN(parsedValue) ? (nullable ? null : 0) : parsedValue);
                    }}
                    onBlur={field.onBlur}
                />
            )}
        />
    );
};

/** Prix unitaire d'une ligne : décimales libres, donc pas de `type="number"` — voir `helpers/decimal`. */
const PriceCell = ({ formIndex, productName }: { formIndex: number; productName: string }) => {
    const { control } = useFormContext<SupplyOrderFormValues>();

    return (
        <Controller
            control={control}
            name={`products.${formIndex}.unitPrice`}
            render={({ field }) => (
                <DecimalInput
                    className="h-7 w-24 text-right"
                    placeholder="0"
                    aria-label={`Prix unitaire HT — ${productName}`}
                    data-test="unit-price-input"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                />
            )}
        />
    );
};

/** Valeur dérivée d'une ligne, recalculée à chaque frappe. */
const DerivedCell = ({
    formIndex,
    render,
}: {
    formIndex: number;
    render: (product: SupplyOrderProductFormValue) => React.ReactNode;
}) => {
    const { control } = useFormContext<SupplyOrderFormValues>();
    const product = useWatch({ control, name: `products.${formIndex}` });
    return <>{product ? render(product) : null}</>;
};

export function getOrderedProductsColumns({
    canEditReceivedDetails,
    minQuantityByProductId,
    productIdsToOrderOnce,
    onEditReceivedDetails,
    onShowConfirmedQuantityHistory,
    confirmedQuantityHistoryByProductId,
    stockVariations,
    orderingTimes,
}: ColumnsOptions): ColumnDef<OrderedProductRow, unknown>[] {
    const columns: ColumnDef<OrderedProductRow, unknown>[] = [
        textColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'product',
            accessorFn: (row) => `${row.name} - ${row.brand} - ${row.volume}`,
            header: 'Produit',
            defaultVisible: true,
            truncate: false,
            cell: ({ row }) => (
                <ProductCell
                    row={row.original}
                    productIdsToOrderOnce={productIdsToOrderOnce}
                    minQuantityByProductId={minQuantityByProductId}
                />
            ),
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'conditionning',
            header: 'Colisage',
            defaultVisible: true,
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'colis',
            header: 'Nb colis',
            defaultVisible: true,
            cell: ({ row }) => (
                <NumberCell
                    name={`products.${row.original.formIndex}.colis`}
                    dataTest="colis-input"
                    ariaLabel={`Nombre de colis — ${row.original.name}`}
                />
            ),
            footer: () => <LiveTotal compute={(products) => products.reduce((total, { colis }) => total + colis, 0)} />,
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'orderedQuantity',
            accessorFn: (row) => getOrderedQuantity(row),
            // Le besoin calculé entre parenthèses : c'est lui qu'on compare à la quantité au moment de
            // choisir les colis, et l'avoir sous les yeux évite l'aller-retour vers « Prévisions ».
            header: 'Qté commandée (préd. / menus)',
            defaultVisible: true,
            cell: ({ row }) => (
                <DerivedCell
                    formIndex={row.original.formIndex}
                    render={(product) => (
                        <span className="whitespace-nowrap">
                            {getOrderedQuantity(product)}{' '}
                            <span className="text-muted-foreground">
                                ({product.quantityGenerated} / {product.quantityGeneratedForMenus})
                            </span>
                        </span>
                    )}
                />
            ),
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'confirmedQuantity',
            header: 'Qté confirmée',
            defaultVisible: true,
            cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <NumberCell
                        name={`products.${row.original.formIndex}.confirmedQuantity`}
                        nullable
                        dataTest="confirmed-quantity-input"
                        ariaLabel={`Quantité confirmée — ${row.original.name}`}
                    />
                    {confirmedQuantityHistoryByProductId.get(row.original._id)?.length ? (
                        <Button
                            type="button"
                            size="icon-xs"
                            variant="ghost"
                            aria-label="Historique des quantités confirmées"
                            onClick={() => onShowConfirmedQuantityHistory(row.original._id)}
                        >
                            <History />
                        </Button>
                    ) : null}
                </div>
            ),
            footer: () => (
                <LiveTotal
                    compute={(products) => products.reduce((total, product) => total + getExpectedQuantity(product), 0)}
                />
            ),
        }),
    ];

    columns.push(
        // Affichée quel que soit l'état de l'achat : savoir ce qui est arrivé ne doit pas demander
        // d'ouvrir le détail des quantités reçues. Seul le CRAYON dépend de l'état.
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'quantityReceived',
            accessorFn: (row) => getSupplyOrderProductReceivedTotal(row.productReceivedDetails).quantityReceived ?? 0,
            header: 'Qté reçue',
            defaultVisible: true,
            cell: ({ row }) => (
                <DerivedCell
                    formIndex={row.original.formIndex}
                    render={(product) => {
                        const { quantityReceived } = getSupplyOrderProductReceivedTotal(product.productReceivedDetails);
                        return (
                            <div className="flex items-center gap-1">
                                <span>{quantityReceived ?? 'N/A'}</span>
                                {canEditReceivedDetails ? (
                                    <Button
                                        type="button"
                                        size="icon-xs"
                                        variant="ghost"
                                        aria-label={`Modifier les quantités reçues — ${product.name}`}
                                        data-test="edit-received-details"
                                        onClick={() => onEditReceivedDetails(row.original.formIndex)}
                                    >
                                        <Pencil />
                                    </Button>
                                ) : null}
                            </div>
                        );
                    }}
                />
            ),
            footer: () => (
                <LiveTotal
                    compute={(products) =>
                        products.reduce(
                            (total, { productReceivedDetails }) =>
                                total +
                                (getSupplyOrderProductReceivedTotal(productReceivedDetails).quantityReceived ?? 0),
                            0,
                        )
                    }
                />
            ),
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'unitPrice',
            header: 'PU HT',
            defaultVisible: true,
            cell: ({ row }) => <PriceCell formIndex={row.original.formIndex} productName={row.original.name} />,
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'linePrice',
            accessorFn: (row) => getProductLinePrice(row),
            header: 'Total HT',
            defaultVisible: true,
            cell: ({ row }) => (
                <DerivedCell
                    formIndex={row.original.formIndex}
                    render={(product) => <span>{formatEuroAmount(getProductLinePrice(product))}</span>}
                />
            ),
            footer: () => (
                <LiveTotal
                    compute={(products) => products.reduce((total, product) => total + getProductLinePrice(product), 0)}
                    format={formatEuroAmount}
                />
            ),
        }),

        // --- Colonnes masquées par défaut, activables via le sélecteur de colonnes ---
        numberColumn<OrderedProductRow, Record<string, unknown>>({ accessorKey: 'stock', header: 'Stock' }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'availableStock',
            header: 'Stock dispo',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'packagesPerBatch',
            header: 'Palettisation',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'palletCount',
            accessorFn: (row) =>
                row.conditionning && row.packagesPerBatch
                    ? Math.round((getOrderedQuantity(row) / row.conditionning / row.packagesPerBatch) * 100) / 100
                    : 0,
            header: 'Nb palettes',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'quantityGenerated',
            header: 'Qté prédictions',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'quantityGeneratedForMenus',
            header: 'Qté menus',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            accessorKey: 'supplierUnitPrice',
            header: 'PU fournisseur',
        }),
    );

    columns.push(
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'conditionningReceived',
            accessorFn: (row) =>
                getSupplyOrderProductReceivedTotal(row.productReceivedDetails).conditionningReceived ?? 0,
            header: 'Colisage reçu',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'colisReceived',
            accessorFn: (row) => getSupplyOrderProductReceivedTotal(row.productReceivedDetails).colisReceived ?? 0,
            header: 'Nb colis reçus',
            cell: ({ row }) => {
                const { colisReceived, conditionningReceived, quantityReceived } = getSupplyOrderProductReceivedTotal(
                    row.original.productReceivedDetails,
                );
                if (colisReceived === null) return <span>N/A</span>;
                // Le reste se prend sur le COLISAGE reçu : c'est lui qui dit combien d'unités tient un colis.
                const remainder = conditionningReceived ? (quantityReceived ?? 0) % conditionningReceived : 0;
                return <span>{remainder ? `${colisReceived} (+${remainder}p)` : colisReceived}</span>;
            },
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'brokenItemsReceived',
            accessorFn: (row) =>
                getSupplyOrderProductReceivedTotal(row.productReceivedDetails).brokenItemsReceived ?? 0,
            header: 'Qté cassée',
        }),
        numberColumn<OrderedProductRow, Record<string, unknown>>({
            id: 'receivedPrice',
            accessorFn: (row) =>
                (getSupplyOrderProductReceivedTotal(row.productReceivedDetails).quantityReceived ?? 0) *
                (row.unitPrice ?? row.supplierUnitPrice),
            header: 'Total HT reçu',
            cell: ({ getValue }) => <span>{formatEuroAmount(getValue() as number)}</span>,
        }),
        // Pourquoi cette quantité ? La réponse est dans les variations de stock, sur la ligne même.
        // En dernier : on lit d'abord combien on commande, l'aperçu vient en appui à droite.
        {
            id: 'stockVariation',
            header: 'Variation de stock',
            enableSorting: false,
            meta: { defaultVisible: true },
            cell: ({ row }) => (
                <StockVariationPreviewCell
                    stockVariations={stockVariations}
                    productId={row.original._id}
                    productInfo={row.original}
                    stock={row.original.stock}
                    orderingTimes={orderingTimes}
                    shouldDisplayTooltip={row.index < 3}
                />
            ),
        },
    );

    return columns;
}

const LiveTotal = ({
    compute,
    format = (value: number) => String(Math.round(value * 100) / 100),
}: {
    compute: (products: SupplyOrderProductFormValue[]) => number;
    format?: (value: number) => string;
}) => {
    const { control } = useFormContext<SupplyOrderFormValues>();
    const products = useWatch({ control, name: 'products' }) ?? [];
    return <span className="font-semibold">{format(compute(products))}</span>;
};

const ProductCell = ({
    row,
    productIdsToOrderOnce,
    minQuantityByProductId,
}: {
    row: OrderedProductRow;
    productIdsToOrderOnce: Set<string>;
    minQuantityByProductId: Map<string, number>;
}) => {
    const { control } = useFormContext<SupplyOrderFormValues>();
    const product = useWatch({ control, name: `products.${row.formIndex}` }) ?? row;

    const warnings = getSupplyOrderProductWarnings({
        product,
        supplierMinQuantity: minQuantityByProductId.get(product._id),
    });

    return (
        <div className="flex min-w-0 items-center gap-2">
            <Link className="truncate hover:underline" to={`/product/${product._id}`}>
                {`${product.name} - ${product.brand} - ${product.volume}`}
            </Link>
            {productIdsToOrderOnce.has(product._id) ? (
                <Badge variant="secondary" className="shrink-0">
                    Demandé par Supply
                </Badge>
            ) : null}
            <WarningIndicator warnings={warnings} dataTest="supply-order-product-warnings" />
        </div>
    );
};
